import { Filter, LogicalAndFilter } from '@cubejs-client/core';
import {
  CUBE_VOC_TO_INTERACTION_LINK,
  CUBE_WEBSITE_AGGREGATE_INTERACTION,
  HCP_CUBE_NAME,
} from '@/api/hcp/interaction/types';
import { StackedBarSegment } from '@/storybook/components/StackedBarSplit/StackedBarSplit.types';
import { MaybePrimitive } from '@/utils/cube/mapper.type';

export const transformAdditionalFilters = <T extends Filter | null | undefined>(inputFilters: T): T | Filter => {
  if (!inputFilters || !('and' in inputFilters) || !inputFilters.and) return inputFilters;

  const websiteNameFilters = inputFilters.and.filter(
    filterItem =>
      'member' in filterItem && typeof filterItem.member === 'string' && filterItem.member.includes('website_name'),
  );

  if (websiteNameFilters.length === 0) {
    return { and: [] };
  }

  return {
    ...inputFilters,
    and: websiteNameFilters.map(filterItem => {
      if ('member' in filterItem && filterItem.member === `${CUBE_WEBSITE_AGGREGATE_INTERACTION}.website_name`) {
        return {
          ...filterItem,
          member: `${CUBE_VOC_TO_INTERACTION_LINK}.website_name`,
        };
      }
      return filterItem;
    }),
  };
};

export const getNonAnonymousHCPsFilters = (
  globalFiltersWithoutHcpCountryCode: { and?: Filter[] | null } | null | undefined,
): Filter[] => {
  if (!globalFiltersWithoutHcpCountryCode?.and?.length) return [];
  // Cube 1.3 notEquals adds `OR column IS NULL` unless values contain null.
  // BinaryFilter.values is string[], so the null exclusion is a separate `set` filter.
  const member = `${CUBE_VOC_TO_INTERACTION_LINK}.voc_hcp_bk`;
  return [
    { member, operator: 'notEquals', values: ['null'] },
    { member, operator: 'set' },
  ];
};

export const getNonAnonymousHcpProfileFilters = (globalFiltersWithoutHcpCountryCode: LogicalAndFilter): Filter[] => {
  if (!globalFiltersWithoutHcpCountryCode?.and?.length) return [];
  return [
    { member: `${HCP_CUBE_NAME}.hcp_bk`, operator: 'set' },
    { member: `${HCP_CUBE_NAME}.last_name`, operator: 'set' },
    { member: `${HCP_CUBE_NAME}.first_name`, operator: 'set' },
  ];
};

export type EmailCustomQuestionByQuestion = {
  question: string;
  isNotOpenQuestion: boolean;
  totalAnswers: number;
  latestDate: string;
  segments: StackedBarSegment[];
};

export type CustomQuestionAnswerRow = {
  question: string;
  answer: string;
  count: number;
  recordedDate: string;
  isNotOpenQuestion: boolean;
};

type QuestionAccumulator = Omit<EmailCustomQuestionByQuestion, 'totalAnswers' | 'segments'> & {
  segments: Map<string, number>;
};

export const parseIsNotOpenQuestion = (flag: MaybePrimitive): boolean => flag === 'true' || Number(flag) === 1;

const pickLatestDate = (current: string, candidate: string): string => {
  if (!candidate) return current;
  if (!current) return candidate;
  return candidate > current ? candidate : current;
};

const accumulateAnswerRows = (rows: CustomQuestionAnswerRow[]): Map<string, QuestionAccumulator> => {
  const grouped = new Map<string, QuestionAccumulator>();

  rows.forEach(({ question, answer, count, recordedDate, isNotOpenQuestion }) => {
    const existing = grouped.get(question);
    if (!existing) {
      grouped.set(question, {
        question,
        isNotOpenQuestion,
        latestDate: recordedDate,
        segments: new Map([[answer, count]]),
      });
      return;
    }
    existing.segments.set(answer, (existing.segments.get(answer) ?? 0) + count);
    existing.latestDate = pickLatestDate(existing.latestDate, recordedDate);
  });

  return grouped;
};

const finalizeQuestion = (accumulator: QuestionAccumulator): EmailCustomQuestionByQuestion => {
  const segments: StackedBarSegment[] = Array.from(accumulator.segments.entries()).map(([label, value]) => ({
    label,
    value,
  }));
  return {
    question: accumulator.question,
    isNotOpenQuestion: accumulator.isNotOpenQuestion,
    latestDate: accumulator.latestDate,
    totalAnswers: segments.reduce((sum, segment) => sum + segment.value, 0),
    segments,
  };
};

export const groupCustomQuestionAnswers = (rows: CustomQuestionAnswerRow[]): EmailCustomQuestionByQuestion[] =>
  Array.from(accumulateAnswerRows(rows).values()).map(finalizeQuestion);
